import {ReactNode, useMemo, useState} from 'react'

import {CustomModalProps, GlobalModalContextType} from '../interfaces'

export const useCustomModalStore = () => {
    const [openedCustomModals, updateCustomModals] = useState<CustomModalProps[]>([])

    const custom = useMemo((): GlobalModalContextType => {
        let prev: CustomModalProps[] = []

        const setData = (data: CustomModalProps[]) => {
            // Reassigning `prev` after render has completed can cause inconsistent behavior on subsequent renders. Consider using state instead.
            // eslint-disable-next-line react-hooks/immutability
            prev = data
            updateCustomModals(data)
        }

        const closeCustomAlert = () => {
            setData(prev.filter((m) => m.type !== 'ALERT'))
        }
        const openCustomAlert = ({
            title,
            description,
            onClose,
        }: {
            title: ReactNode
            description?: ReactNode
            onClose?: () => void
        }) => {
            setData([
                ...prev,
                {
                    type: 'ALERT',
                    title,
                    description,
                    onClose: () => {
                        closeCustomAlert()
                        onClose?.()
                    },
                },
            ])
        }
        const closeCustomConfirm = () => {
            setData(prev.filter((m) => m.type !== 'CONFIRM'))
        }
        const openCustomConfirm = ({
            title,
            description,
            onCancelClick,
            onConfirmClick,
        }: {
            title: ReactNode
            description?: ReactNode
            onCancelClick?: () => void
            onConfirmClick?: () => void
        }) => {
            setData([
                ...prev,
                {
                    type: 'CONFIRM',
                    title,
                    description,
                    onCancelClick: () => {
                        closeCustomConfirm()
                        onCancelClick?.()
                    },
                    onConfirmClick: () => {
                        closeCustomConfirm()
                        onConfirmClick?.()
                    },
                },
            ])
        }
        const asyncCustomAlert = ({title, description}: {title: ReactNode; description?: ReactNode}) => {
            return new Promise<boolean>((resolve) => {
                openCustomAlert({
                    title,
                    description,
                    onClose: () => {
                        resolve(true)
                    },
                })
            })
        }
        const asyncCustomConfirm = ({title, description}: {title: ReactNode; description?: ReactNode}) => {
            return new Promise<boolean>((resolve) => {
                openCustomConfirm({
                    title,
                    description,
                    onCancelClick: () => {
                        resolve(false)
                    },
                    onConfirmClick: () => {
                        resolve(true)
                    },
                })
            })
        }

        return {
            isSetup: true,
            alert: {
                open: openCustomAlert,
                close: closeCustomAlert,
                asyncOpen: asyncCustomAlert,
            },
            confirm: {
                open: openCustomConfirm,
                close: closeCustomConfirm,
                asyncOpen: asyncCustomConfirm,
            },
        }
    }, [])

    return {
        openedCustomModals,
        custom,
    }
}
